import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useDelete, useGetIdentity, useShow } from "@refinedev/core";
import { useParams, useNavigate } from "react-router-dom";
import ChatBubble from "@mui/icons-material/ChatBubble";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import Phone from "@mui/icons-material/Phone";
import Place from "@mui/icons-material/Place";
import Star from "@mui/icons-material/Star";

import { CustomButton } from "components";

function getNumberOfStarsBasedOnNeed(needLevel: any) {
  switch (needLevel) {
    case "Low":
      return 2;
    case "Average":
      return 3;
    case "High":
      return 4;
    case "Urgent":
      return 5;
    default:
      return 0;
  }
}

function checkImage(url: any) {
  const img = new Image();
  img.src = url;
  return img.width !== 0 && img.height !== 0;
}

const ChildDetails = () => {
  const navigate = useNavigate();
  const { data: user } = useGetIdentity({
    v3LegacyAuthProviderCompatible: true,
  });
  const { queryResult } = useShow();
  const { mutate } = useDelete();
  const { id } = useParams();

  const { data, isLoading, isError } = queryResult;

  const childDetails = data?.data ?? {};

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError) {
    return <div>Something went wrong!</div>;
  }

  const isCurrentUser = user.email === childDetails.creator.email;

  const handleDeleteChild = () => {
    const response = confirm("Are you sure you want to delete this child?");
    if (response) {
      mutate(
        {
          resource: "children",
          id: id as string,
        },
        {
          onSuccess: () => {
            navigate("/children");
          },
        }
      );
    }
  };

  return (
    <Box
      borderRadius="15px"
      padding="20px"
      bgcolor="#FCFCFC"
      width="fit-content"
    >
      <Typography fontSize={25} fontWeight={700} color="#11142D">
        Child Details
      </Typography>

      <Box
        mt="20px"
        display="flex"
        flexDirection={{ xs: "column", lg: "row" }}
        gap={4}
      >
        <Box flex={1} maxWidth={764}>
          <img
            src={childDetails.photo}
            alt="child_details-img"
            height={546}
            style={{ objectFit: "cover", borderRadius: "10px" }}
            className="child_details-img"
          />

          <Box mt="15px">
            <Stack
              direction="row"
              justifyContent="space-between"
              flexWrap="wrap"
              alignItems="center"
            >
              <Typography
                fontSize={22}
                fontWeight={600}
                mt="10px"
                color="#11142D"
              >
                {childDetails.name}
              </Typography>
              <Typography
                  fontSize={18}
                  fontWeight={600}
                  color="#475BE8"
                  textTransform="capitalize"
                >
                  Age: {childDetails.age}
                </Typography>
            </Stack>

            <Stack
              direction="row"
              flexWrap="wrap"
              justifyContent="space-between"
              alignItems="center"
              gap={2}
            >
              <Box>
                <Typography
                  fontSize={16}
                  fontWeight={500}
                  mt="10px"
                  color="#11142D"
                >
                  Level Of Need
                </Typography>
                <Stack direction="row" alignItems="flex-end" gap={1}>
                  <Typography fontSize={18} fontWeight={600} color="#475BE8">
                    {childDetails.levelOfNeed.toUpperCase()}
                  </Typography>
                </Stack>
              </Box>

              <Box>
                <Stack mt={0.5} direction="row" alignItems="center" gap={0.5}>
                <Typography fontSize={16} fontWeight={500} color="#808191">
                    Class:
                  </Typography>
                  <Typography fontSize={14} fontWeight={500} color="#808191">
                    {childDetails.grade}
                  </Typography>
                </Stack>
              </Box>

            </Stack>

            <Stack mt="14px" direction="column" gap="10px">
              <Typography fontSize={18} fontWeight={400} color="#11142D">
                Description
              </Typography>
              <Typography fontSize={15} fontWeight={400} color="#808191">
                {childDetails.description}
              </Typography>
            </Stack>
          </Box>
        </Box>

        <Box
          width="100%"
          flex={1}
          maxWidth={326}
          display="flex"
          flexDirection="column"
          gap="20px"
        >
          <Stack
            width="100%"
            p={2}
            direction="column"
            justifyContent="center"
            alignItems="center"
            border="1px solid #E4E4E4"
            borderRadius={2}
          >
            <Stack
              mt={2}
              justifyContent="center"
              alignItems="center"
              textAlign="center"
            >
              <img
                src={
                  checkImage(childDetails.creator.avatar)
                    ? childDetails.creator.avatar
                    : "https://upload.wikimedia.org/wikipedia/commons/thumb/5/59/User-avatar.svg/2048px-User-avatar.svg.png"
                }
                alt="avatar"
                width={90}
                height={90}
                style={{
                  borderRadius: "100%",
                  objectFit: "cover",
                }}
              />

              <Box mt="15px">
                <Typography fontSize={18} fontWeight={600} color="#11142D">
                  {childDetails.creator.name}
                </Typography>
                <Typography
                  mt="5px"
                  fontSize={14}
                  fontWeight={400}
                  color="#808191"
                >
                  Agent
                </Typography>
              </Box>

              <Typography mt={1} fontSize={16} fontWeight={600} color="#11142D">
                {childDetails.creator.allChildren.length} Children
              </Typography>
            </Stack>

            <Stack
              width="100%"
              mt="25px"
              direction="row"
              flexWrap="wrap"
              gap={2}
            >
              <CustomButton
                title={!isCurrentUser ? "Message" : "Edit"}
                backgroundColor="#475BE8"
                color="#FCFCFC"
                fullWidth
                icon={!isCurrentUser ? <ChatBubble /> : <Edit />}
                handleClick={() => {
                  if (isCurrentUser) {
                    navigate(`/children/edit/${childDetails._id}`);
                  }
                }}
              />
              <CustomButton
                title={!isCurrentUser ? "Call" : "Delete"}
                backgroundColor={!isCurrentUser ? "#2ED480" : "#d42e2e"}
                color="#FCFCFC"
                fullWidth
                icon={!isCurrentUser ? <Phone /> : <Delete />}
                handleClick={() => {
                  if (isCurrentUser) handleDeleteChild();
                }}
              />
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default ChildDetails;
